import { createAuth0Client } from "@auth0/auth0-spa-js";
import type { Auth0Client, User } from "@auth0/auth0-spa-js";
import type { AppConfig } from "./config.js";

/**
 * Thin wrapper over @auth0/auth0-spa-js (Authorization Code + PKCE). The SPA is
 * gated behind login; every API call attaches a fresh access token acquired for
 * the configured audience.
 */
export interface Auth {
  readonly authenticated: boolean;
  readonly user: User | undefined;
  login(): Promise<void>;
  logout(): void;
  getToken(): Promise<string>;
}

/** Factory for the Auth0 client; overridable in tests. */
export type CreateAuth0Client = typeof createAuth0Client;

export async function initAuth(
  config: AppConfig,
  createClient: CreateAuth0Client = createAuth0Client,
): Promise<Auth> {
  const client: Auth0Client = await createClient({
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    authorizationParams: {
      audience: config.auth0Audience,
      redirect_uri: window.location.origin,
    },
    useRefreshTokens: true,
    cacheLocation: "localstorage",
  });

  // Complete the redirect leg of the PKCE flow, then restore a clean URL.
  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) {
    await client.handleRedirectCallback();
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  let authenticated = await client.isAuthenticated();
  let user = authenticated ? await client.getUser() : undefined;

  return {
    get authenticated() {
      return authenticated;
    },
    get user() {
      return user;
    },
    async login() {
      await client.loginWithRedirect();
    },
    logout() {
      authenticated = false;
      user = undefined;
      client.logout({ logoutParams: { returnTo: window.location.origin } });
    },
    async getToken() {
      try {
        const token = await client.getTokenSilently();
        authenticated = true;
        if (!user) user = await client.getUser();
        return token;
      } catch {
        // The cached session can no longer mint an access token (refresh token
        // missing, expired or revoked). Bounce the owner back to login rather
        // than surface a raw Auth0 error. The redirect navigates away, so this
        // promise intentionally never settles and no caller sees a token error.
        authenticated = false;
        await client.loginWithRedirect();
        return new Promise<string>(() => {});
      }
    },
  };
}
